import { BOMComponent } from '@/types/bom';
import { logger } from './logger';

/**
 * Helper function to convert flat components array to hierarchical structure
 */
export const buildComponentHierarchy = (flatComponents: BOMComponent[]): BOMComponent[] => {
  logger.debug(`Building hierarchy from ${flatComponents.length} components`);
  
  // Create a map of ID to component for quick lookup
  const componentMap = new Map<string, BOMComponent>();
  flatComponents.forEach(component => {
    componentMap.set(component.id, {...component, children: []});
  });
  
  // Build hierarchy
  const rootComponents: BOMComponent[] = [];
  
  flatComponents.forEach(component => {
    const mappedComponent = componentMap.get(component.id)!;
    
    if (component.parent_component_id) {
      // This is a child component, add it to its parent
      const parent = componentMap.get(component.parent_component_id);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(mappedComponent);
      } else {
        // Orphaned component (parent missing), add to root
        logger.warn(`Orphaned component found: ${component.id}, parent ${component.parent_component_id} is missing`);
        rootComponents.push(mappedComponent);
      }
    } else {
      // This is a root component
      rootComponents.push(mappedComponent);
    }
  });
  
  // Sort all children by level, sequence_order, and then name
  const sortChildren = (components: BOMComponent[]) => {
    components.sort((a, b) => {
      // First sort by level
      if (a.level !== b.level) return a.level - b.level;
      
      // Then sort by sequence_order if both have it defined
      if (a.sequence_order !== undefined && b.sequence_order !== undefined) {
        return a.sequence_order - b.sequence_order;
      }
      
      // If only one has sequence_order, prioritize it
      if (a.sequence_order !== undefined) return -1;
      if (b.sequence_order !== undefined) return 1;
      
      // Fallback to alphabetical sorting by name
      return a.component_name.localeCompare(b.component_name);
    });
    
    // Recursively sort children
    components.forEach(component => {
      if (component.children && component.children.length > 0) {
        sortChildren(component.children);
      }
    });
  };
  
  sortChildren(rootComponents);
  logger.debug(`Hierarchy built with ${rootComponents.length} root components`);
  return rootComponents;
};

/**
 * Safely converts a value to UUID string format for database operations
 */
export const safeUUID = (value: any): string | null => {
  if (!value) return null;
  
  if (typeof value === 'string') {
    // Check if it's a valid UUID format
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
      return value;
    }
    logger.warn(`Invalid UUID format: ${value}`);
    return null;
  }
  
  // Try to convert to string if it's not already
  try {
    const strValue = String(value);
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(strValue)) {
      return strValue;
    }
  } catch (e) {
    logger.error(`Failed to convert to UUID: ${e}`);
  }
  
  return null;
};

/**
 * Safely handle array values for PostgreSQL operations
 */
export const safeArray = <T>(arr: T[] | null | undefined): T[] | null => {
  if (!arr) return null;
  if (!Array.isArray(arr)) return null;
  
  // Filter out null/undefined values and ensure items are strings
  const filtered = arr.filter(item => item !== null && item !== undefined);
  if (filtered.length === 0) return null;
  
  return filtered;
};

/**
 * Clean object by removing null/undefined/empty values
 */
export const cleanObject = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const cleaned: Partial<T> = { ...obj };
  
  Object.entries(cleaned).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      delete cleaned[key as keyof T];
    }
  });
  
  return cleaned;
};

/**
 * Safely escape a string for SQL injection prevention
 */
export const escapeSqlString = (str: string): string => {
  if (typeof str !== 'string') return '';
  return str.replace(/'/g, "''");
};

/**
 * Automatically find a matching raw material for a component
 * by searching through design_raw_materials data
 * 
 * @param designId The design ID to search materials for
 * @param componentName The component name to look for
 * @param materialType Optional material type to match
 * @returns Promise with the matched raw material or null if no match
 */
export const findMatchingRawMaterial = async (
  designId: string,
  componentName: string,
  materialType?: string,
): Promise<{
  id: string;
  material: string;
  drawing_number?: string;
  finishing_size?: string;
  raw_material_size?: string;
  unit?: string;
} | null> => {
  const { supabase } = await import('@/lib/supabase/client');
  const { logger } = await import('./logger');
  
  try {
    logger.debug(`Finding raw material for ${componentName} (material: ${materialType || 'any'})`);
    
    // Get design files
    const { data: designData, error: designError } = await supabase
      .from('designs')
      .select('id, files:design_files(*)')
      .eq('id', designId)
      .single();
      
    if (designError || !designData?.files || designData.files.length === 0) {
      logger.debug(`No design files found for design ${designId}`);
      return null;
    }
    
    // Get file IDs
    const fileIds = designData.files.map((file: any) => file.id);
    
    // Get raw materials for these files
    const { data: materialsData, error: materialsError } = await supabase
      .from('design_raw_materials')
      .select('*')
      .in('file_id', fileIds);
      
    if (materialsError || !materialsData || materialsData.length === 0) {
      logger.debug(`No materials found for design ${designId}`);
      return null;
    }
    
    // Normalize search terms for better matching
    const normalizedComponentName = componentName.toLowerCase().trim();
    const normalizedMaterialType = materialType ? materialType.toLowerCase().trim() : '';
    
    // Search through all materials to find a match
    for (const materialEntry of materialsData) {
      // Skip if no materials array or it's empty
      if (!materialEntry.materials || !Array.isArray(materialEntry.materials) || materialEntry.materials.length === 0) {
        continue;
      }
      
      // Search through each material in the array
      for (const material of materialEntry.materials) {
        const materialPartName = String(material.part_name || '').toLowerCase().trim();
        const materialType = String(material.material || '').toLowerCase().trim();
        
        // Check for exact match on name, or contains match for non-exact
        const nameMatches = 
          materialPartName === normalizedComponentName ||
          materialPartName.includes(normalizedComponentName) ||
          normalizedComponentName.includes(materialPartName);
          
        // Check for material type match if provided
        const materialMatches = 
          !normalizedMaterialType || 
          materialType === normalizedMaterialType ||
          materialType.includes(normalizedMaterialType) ||
          normalizedMaterialType.includes(materialType);
          
        if (nameMatches && materialMatches) {
          logger.debug(`Found matching material: ${materialPartName} (${materialType})`);
          
          // Create a standardized return object with the found material
          return {
            id: materialEntry.id,  // The design_raw_materials record ID
            material: material.material || '',
            drawing_number: material.material || '', // Often the drawing number is same as material code
            finishing_size: material.finishing_size || '',
            raw_material_size: material.raw_material_size || '',
            unit: material.unit || 'Nos',
          };
        }
      }
    }
    
    logger.debug(`No matching material found for ${componentName}`);
    return null;
  } catch (error) {
    logger.error(`Error finding matching raw material: ${error}`);
    return null;
  }
};
